import type { CompletionItem } from "./completion-picker";
import { finalizeDoctorNotice } from "./doctor-notice";

// TES-1317 (S4 of TES-1311) — "Vad händer nu?", the system-generated part of
// what the patient receives after the care meeting. A-02 "FÖRTYDLIGANDE
// 2026-10-13 — MÖTESLÄGET OCH PATIENTFÖRHANDSVISNINGEN":
//   "Vad händer nu? är patientvänd, systemgenererad information. Den ska
//   inte skrivas manuellt av läkaren. Läkaren kontrollerar endast att den
//   automatiskt genererade informationen stämmer innan publicering."
//   "Systemet genererar Vad händer nu? från valt utfall,
//   abonnemangsstatus, valda kompletteringar och verifierad receptstatus"
// CP-09 "FÖRTYDLIGANDE 2026-10-13 — SYSTEMGENERERAT VAD HÄNDER NU?":
//   "Komplettering: endast de faktiskt valda kompletteringarna visas."
//
// Pure, so the admin's step 5 preview and the server's publish compute the
// same text from the same facts. The server never takes the text from the
// browser: it recomputes it from what it stores (`whatHappensNext`) and
// appends it to the patient text it publishes (`withWhatHappensNext`).
//
// Ö-527 (open) — A-02 gives each variant as an instruction, not finished
// copy. Where CP-09 or CP-06 has an approved sentence it is used verbatim;
// the rest is ours, written from A-02's instruction, and listed in the PR.

export const WHAT_HAPPENS_NEXT_HEADING = "Vad händer nu?";

/**
 * The outcome as the patient's package states it.
 *   - `approved`: "Godkänd för behandling", the decision published with the
 *     meeting (TES-1185) or on the decision workspace.
 *   - `proceed_undecided`: the meeting proceeds and the decision is taken
 *     later (the unticked "Publicera behandlingsbeslutet nu", Ö-526).
 *   - `completion`: "Komplettering behövs".
 *   - `cannot_proceed`: "Kan inte gå vidare".
 */
export type PatientPackageOutcome =
  | "approved"
  | "proceed_undecided"
  | "completion"
  | "cannot_proceed";

export interface WhatHappensNextInput {
  outcome: PatientPackageOutcome;
  /** Entitled subscription (trialing or active), the one entitlement rule. */
  subscriptionActive: boolean;
  /** The chosen completions, on `completion`. */
  items: readonly CompletionItem[];
  /** The remiss tick ("provbeställning/remiss … skapad i Webdoc"). */
  labOrderConfirmed: boolean;
}

export const WHAT_HAPPENS_NEXT_LINE = {
  // CP-09 "KANONISKT APPLÄGE 2026-10-13", §1 "Nästa steg: aktivera ditt
  // abonnemang", verbatim; "din e-post" from CP-09's approved Ella Support
  // message, since a notice body carries no masked address.
  activateSubscription:
    "För att behandlingen ska kunna starta behöver ditt abonnemang aktiveras.",
  activationMail:
    "Information om hur du gör har skickats till din e-post. Hittar du inte mejlet, kontrollera även skräpposten.",
  // Ours, from A-02 "behandlingen finns under Din behandling; hämta ut
  // läkemedlet när receptstatusen tillåter det" and CP-09 "får
  // uthämtningsinformation först när receptstatusen tillåter det".
  treatmentInApp: "Du hittar din behandling under Din behandling i appen.",
  pickup:
    "Där ser du också receptstatusen och när du kan hämta ut läkemedlet på apoteket.",
  // CP-06's neutral waiting line, verbatim (the prefill's `nextStepInApp`).
  proceedUndecided: "Du får besked i appen när nästa steg är klart.",
  // CP-09 "Komplettering krävs", "Nästa steg: Se och slutför uppgiften som
  // visas i appen.", in the plural.
  completion: "Se och slutför uppgifterna som visas i appen.",
  // Ours, from A-02 "Nytt blodprov krävs: betala när betalning krävs, läs
  // förberedelserna, hitta provtagningsställe och bekräfta efter
  // provtagningen."
  newBloodTest:
    "Blodprov: betala provet när appen ber om det, läs förberedelserna, hitta ett provtagningsställe och bekräfta i appen när du har lämnat provet.",
  // Ours, from A-02 "Blodtryck och vilopuls krävs: läs instruktionen och
  // registrera värdena."
  bloodPressurePulse:
    "Blodtryck och vilopuls: läs instruktionen i appen och registrera dina värden.",
  // The blood test's reported note (TES-942), verbatim second sentence.
  afterBloodTest:
    "Din läkare återkommer med besked och behandlingsbeslut när provsvaret har kommit.",
  // CP-09 "Beslutsläge C", verbatim.
  afterCompletion:
    "När kompletteringen är mottagen fortsätter läkaren bedömningen.",
  // CP-09 "Beslutsläge B", verbatim.
  readDoctorsNotice:
    "Läs läkarens personliga förklaring och vilken väg som rekommenderas härnäst.",
  // Ours, from A-02 "ingen behandlingsuppgift skapas".
  noTreatmentTasks: "Inga behandlingsuppgifter skapas i appen.",
} as const;

/** The lines under "Vad händer nu?", in the order the patient reads them. */
export function whatHappensNext(input: WhatHappensNextInput): string[] {
  const L = WHAT_HAPPENS_NEXT_LINE;
  switch (input.outcome) {
    case "approved":
      return input.subscriptionActive
        ? [L.treatmentInApp, L.pickup]
        : [L.activateSubscription, L.activationMail];
    case "proceed_undecided":
      return [L.proceedUndecided];
    case "cannot_proceed":
      return [L.readDoctorsNotice, L.noTreatmentTasks];
    case "completion": {
      // "Patienten får inte få uppgiften att lämna blodprov innan denna
      // bekräftelse finns" (A-02), as in the notice's prefill.
      const bloodTest =
        input.items.includes("new_blood_test") && input.labOrderConfirmed;
      const out: string[] = [L.completion];
      if (bloodTest) out.push(L.newBloodTest);
      if (input.items.includes("blood_pressure_pulse"))
        out.push(L.bloodPressurePulse);
      out.push(bloodTest ? L.afterBloodTest : L.afterCompletion);
      return out;
    }
  }
}

/**
 * The patient text as it is published: the doctor's text with its empty
 * headings dropped (`finalizeDoctorNotice`), then the generated
 * "Vad händer nu?" block. With no text of the doctor's, the block alone.
 */
export function withWhatHappensNext(
  doctorText: string,
  lines: readonly string[],
): string {
  const text = finalizeDoctorNotice(doctorText);
  if (lines.length === 0) return text;
  const block = [WHAT_HAPPENS_NEXT_HEADING, ...lines].join("\n");
  return text ? `${text}\n\n${block}` : block;
}

// --- contradiction warnings ----------------------------------------------
//
// A-02: "Systemet ska varna vid motsägelser, exempelvis om
// mötessammanfattningen säger att beslut avvaktar samtidigt som patienten är
// godkänd." A warning, never a refusal: the doctor may publish after reading
// it. Like `doctorNoticeUnconfirmedClaims`, a negated sentence is not the
// claim ("Ingen behandling påbörjas", "beslutet avvaktar inte").

export type PatientPackageContradiction =
  | "approved_but_preliminary"
  | "completion_but_approved"
  | "cannot_proceed_but_treatment_starts"
  | "doctor_wrote_what_happens_next";

// TES-1317's list for an approval: "preliminär", "villkorad", "avvaktar",
// "slutligt beslut".
const PRELIMINARY =
  /\b(preliminär\w*|villkora\w*|avvakta\w*|slutlig\w* beslut\w*)\b/i;
// A komplettering text that says the treatment is approved.
const APPROVED = /\b(godkän(d|t|da|ts|des)|beviljad\w*)\b/i;
// A "Kan inte gå vidare" text that announces the treatment starting.
const TREATMENT_STARTS =
  /\b(börja|starta|påbörja)\w*\b[^.!?\n]*\bbehandling\w*|\bbehandling\w*\b[^.!?\n]*\b(börja|starta|påbörja)\w*|\bhämta ut\b|\bförsta dos\w*/i;

const NEGATION = /\b(inte|ingen|inget|inga|aldrig)\b/i;

function sentences(text: string): string[] {
  return text.split(/(?<=[.!?])\s+|\n+/).filter((s) => s.trim());
}

function says(texts: readonly string[], re: RegExp): boolean {
  return texts.some((t) =>
    sentences(t).some((s) => re.test(s) && !NEGATION.test(s)),
  );
}

/**
 * What in the doctor's own texts (the summary, the comment) contradicts the
 * chosen outcome. The generated block is not checked: it follows the
 * outcome by construction.
 */
export function patientPackageContradictions(input: {
  outcome: PatientPackageOutcome | null;
  texts: readonly string[];
}): PatientPackageContradiction[] {
  const out: PatientPackageContradiction[] = [];
  const { outcome, texts } = input;
  if (outcome === "approved" && says(texts, PRELIMINARY))
    out.push("approved_but_preliminary");
  if (outcome === "completion" && says(texts, APPROVED))
    out.push("completion_but_approved");
  if (outcome === "cannot_proceed" && says(texts, TREATMENT_STARTS))
    out.push("cannot_proceed_but_treatment_starts");
  if (
    texts.some((t) =>
      t.split("\n").some((l) => l.trim() === WHAT_HAPPENS_NEXT_HEADING),
    )
  )
    out.push("doctor_wrote_what_happens_next");
  return out;
}

// What the doctor reads above the publish button. Ours; listed in the PR.
export const PATIENT_PACKAGE_CONTRADICTION_TEXT: Record<
  PatientPackageContradiction,
  string
> = {
  approved_but_preliminary:
    "Patienten är godkänd, men texten beskriver bedömningen som preliminär, villkorad eller att beslut avvaktar.",
  completion_but_approved:
    "Komplettering behövs, men texten säger att behandlingen är godkänd eller kan börja.",
  cannot_proceed_but_treatment_starts:
    "Utfallet är Kan inte gå vidare, men texten beskriver att behandlingen startar.",
  doctor_wrote_what_happens_next:
    "Texten har en egen rubrik Vad händer nu?. Den skapas automatiskt nedan, så patienten får den två gånger.",
};
